import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSyncAlt,
   faUser, faCalendarAlt,
  faEye, faFilter, faTimes, faAnglesLeft, faAnglesRight,
  faChevronLeft, faChevronRight, faClock, faBuilding
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { Link } from 'react-router-dom';
import InvoiceView from './InvoiceView';

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('FacturesCacheDB');
db.version(1).stores({ factures: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedFactures(key, allowExpired = false) {
  try {
    const entry = await db.factures.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedFactures(key, data) {
  await db.factures.put({ key, data, timestamp: Date.now() });
}
async function getStaleFactures(key) {
  return getCachedFactures(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}factures/api_facture.php`;

// ============================================================
// HELPERS
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
  }
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return null;
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageFactures = ({ userId }) => {
  // --- États ---
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState('cache');

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    statut: '',
    date_debut: '',
    date_fin: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 12 });

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    id: '', idcl: '', date: '', echeance: '', montantTotal: 0, statut: 'Envoyée', details: ''
  });

  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [selectedFile, setSelectedFile] = useState(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);

  // ============================================================
  // TOAST
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES FACTURES (avec filtres)
  // ============================================================
  const fetchFactures = useCallback(async (page = 1, filtersObj = null, forceRefresh = false, retryCount = 0) => {
    const currentFilters = filtersObj || filters;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Construire la clé de cache
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', pagination.limit);
    if (currentFilters.search) params.append('search', currentFilters.search);
    if (currentFilters.statut) params.append('statut', currentFilters.statut);
    if (currentFilters.date_debut) params.append('date_debut', currentFilters.date_debut);
    if (currentFilters.date_fin) params.append('date_fin', currentFilters.date_fin);
    const cacheKey = params.toString() || 'default';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setFactures(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives.', 'danger');
      }
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await getCachedFactures(cacheKey);
          if (cached) {
            if (isMounted.current) {
              setFactures(cached.data);
              setPagination(cached.pagination);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            return cached;
          }
        }

        const url = `${API_URL}?action=fetch&${params.toString()}&unique=${new Date().getTime()}`;
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStaleFactures(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setFactures(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('429 – Cache utilisé', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchFactures(page, currentFilters, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleFactures(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setFactures(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Cache utilisé', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const payload = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setFactures(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCachedFactures(cacheKey, payload);
            if (forceRefresh) showToast('Données actualisées', 'success');
            setLoading(false);
            setRefreshing(false);
          }
          return payload;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStaleFactures(cacheKey);
        if (stale && isMounted.current) {
          setFactures(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur – Cache utilisé', 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
    }
  }, [pagination.limit, filters, showToast]);

  // ============================================================
  // GESTION DES FILTRES
  // ============================================================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchFactures(1, filters, false);
  };

  const resetFilters = () => {
    const emptyFilters = { search: '', statut: '', date_debut: '', date_fin: '' };
    setFilters(emptyFilters);
    fetchFactures(1, emptyFilters, true);
  };

  const clearCache = async () => {
    await db.factures.clear();
    showToast('Cache vidé', 'success');
    fetchFactures(1, filters, true);
  };

  // ============================================================
  // RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setFilters(prev => ({ ...prev, search: val }));
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchFactures(1, { ...filters, search: val }, false);
    }, 400);
  };

  // ============================================================
  // CRUD
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const action = editingFacture ? 'update' : 'create';
    const method = editingFacture ? 'PUT' : 'POST';
    const payload = { ...formData, userId };

    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        await db.factures.clear();
        fetchFactures(pagination.currentPage, filters, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        await db.factures.clear();
        fetchFactures(pagination.currentPage, filters, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showToast('Veuillez sélectionner un fichier CSV', 'danger');
      return;
    }
    setSubmitting(true);
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);
    try {
      const data = await fetchJSON(`${API_URL}?action=import`, {
        method: 'POST',
        body: formDataFile
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowImportModal(false);
        setSelectedFile(null);
        await db.factures.clear();
        fetchFactures(1, filters, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur lors de l\'import', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // OUVERTURE MODALES
  // ============================================================
  const openInvoiceModal = (id) => { setInvoiceId(id); setShowInvoiceModal(true); };
  const openEditModal = (item) => {
    setEditingFacture(item);
    setFormData({
      id: item.id,
      idcl: item.idcl,
      date: item.date,
      echeance: item.echeance,
      montantTotal: parseFloat(item.montantTotal),
      statut: item.statut,
      details: item.details
    });
    setShowModal(true);
  };
  const openCreateModal = () => {
    setEditingFacture(null);
    setFormData({ id: '', idcl: '', date: '', echeance: '', montantTotal: 0, statut: 'Envoyée', details: '' });
    setShowModal(true);
  };

  // ============================================================
  // CALCULS
  // ============================================================
  const computeHT = (ttc) => (ttc / 1.16).toFixed(2);
  const computeTVA = (ttc) => (ttc - ttc / 1.16).toFixed(2);

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!factures.length) {
      showToast('Aucune facture à exporter.', 'warning');
      return;
    }
    const headers = ['ID', 'Client', 'Date', 'Échéance', 'Montant HT', 'TVA (16%)', 'Total TTC', 'Statut', 'Détails'];
    const rows = factures.map(f => [
      f.id,
      f.client_nom || f.idcl,
      f.date,
      f.echeance,
      computeHT(f.montantTotal),
      computeTVA(f.montantTotal),
      parseFloat(f.montantTotal).toFixed(2),
      f.statut,
      f.details
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `factures_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // INITIALISATION
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchFactures(1, filters, false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // RENDU CONDITIONNEL
  // ============================================================
  if (loading && !factures.length && !refreshing) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-secondary mt-2">Chargement des factures...</p>
      </div>
    );
  }

  // ============================================================
  // RENDU PRINCIPAL
  // ============================================================
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.2rem;
          margin-top: 0.5rem;
        }

        .facture-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: var(--radius);
          padding: 1.2rem;
          transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        .facture-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent-blue), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .facture-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(59, 130, 246, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.3);
        }
        .facture-card:hover::before {
          opacity: 1;
        }

        .facture-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        .facture-card .card-id {
          font-size: 0.7rem;
          color: var(--text-muted);
          background: rgba(255,255,255,0.04);
          padding: 2px 10px;
          border-radius: 12px;
          font-weight: 600;
        }
        .facture-card .statut-badge {
          padding: 2px 10px;
          border-radius: 20px;
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .facture-card .statut-badge.Envoyée {
          background: rgba(59,130,246,0.2);
          color: #3b82f6;
        }
        .facture-card .statut-badge.Payée {
          background: rgba(16,185,129,0.2);
          color: #10b981;
        }
        .facture-card .statut-badge.Impayée {
          background: rgba(239,68,68,0.2);
          color: #ef4444;
        }

        .facture-card .client-name {
          font-weight: 600;
          color: #fff;
          font-size: 1.05rem;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
        }
        .facture-card .dates {
          display: flex;
          gap: 12px;
          font-size: 0.8rem;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }
        .facture-card .dates span {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .facture-card .montants {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          border-top: 1px solid rgba(255,255,255,0.04);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          margin: 6px 0 8px;
        }
        .facture-card .montants .ht {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        .facture-card .montants .ttc {
          font-size: 1.1rem;
          font-weight: 700;
          color: #fff;
        }
        .facture-card .details {
          font-size: 0.8rem;
          color: var(--text-muted);
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .facture-card .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 6px;
          margin-top: auto;
          padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }

        .filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: flex-end;
          margin-top: 12px;
        }
        .filter-group {
          flex: 1 1 160px;
          min-width: 120px;
        }
        .filter-group label {
          display: block;
          font-size: 0.7rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .filter-group input,
        .filter-group select {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 8px 14px;
          color: var(--text-primary);
          font-size: 0.9rem;
          outline: none;
          transition: 0.3s;
        }
        .filter-group input:focus,
        .filter-group select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .filter-group input::placeholder {
          color: var(--text-muted);
        }
        .filter-group select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 1.5rem;
          padding-top: 1.2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-wrapper .info {
          color: var(--text-secondary);
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .pagination-wrapper .info strong {
          color: var(--text-primary);
        }
        .pagination-buttons {
          display: flex;
          gap: 4px;
          align-items: center;
          flex-wrap: wrap;
        }
        .pagination-buttons button {
          padding: 6px 12px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: 0.2s;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          min-height: 36px;
          cursor: pointer;
          min-width: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pagination-buttons button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .pagination-buttons button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-buttons button.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .pagination-buttons .page-ellipsis {
          padding: 0 6px;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent; border: none; color: var(--text-muted);
          cursor: pointer; font-size: 1.2rem; padding: 0 4px; margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .mt-2 { margin-top: 0.5rem; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.8rem); }
        .display-6 { font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: 700; }
        .m-0 { margin: 0; }
        .p-3 { padding: 1rem; }
        .p-0 { padding: 0; }
        .overflow-hidden { overflow: hidden; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }

        @media (max-width: 768px) {
          .monetra-page { padding: 12px; }
          .glass-card { padding: 1rem; }
          .flex-between { flex-direction: column; align-items: stretch; }
          .filter-bar { flex-direction: column; align-items: stretch; }
          .filter-group { flex: 1 1 100%; }
          .cards-grid { grid-template-columns: 1fr; }
          .card-actions { flex-wrap: wrap; }
          .card-actions .monetra-btn { flex: 1; }
        }
        @media (max-width: 480px) {
          .monetra-btn { font-size: 0.65rem; padding: 4px 10px; min-height: 34px; }
          .facture-card { padding: 0.8rem; }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">📄 Gestion des Factures</h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Suivez vos factures et la TVA (16%)
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn outline" onClick={() => fetchFactures(pagination.currentPage, filters, false)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn outline" onClick={() => fetchFactures(pagination.currentPage, filters, true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn outline" onClick={clearCache} disabled={loading}>
              <FontAwesomeIcon icon={faTrash} /> Vider cache
            </button>
            <button className="monetra-btn" onClick={exportCSV}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </button>
            <button className="monetra-btn" onClick={() => setShowImportModal(true)}>
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
              <input
                type="text"
                placeholder="Rechercher (client, statut, détails...)"
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button className="monetra-btn" onClick={() => setShowFilters(!showFilters)}>
                <FontAwesomeIcon icon={faFilter} /> {showFilters ? 'Masquer filtres' : 'Afficher filtres'}
              </button>
              {showFilters && (
                <button className="monetra-btn" onClick={resetFilters}>
                  <FontAwesomeIcon icon={faTimes} /> Réinitialiser
                </button>
              )}
              <span className="text-secondary small">
                <strong>{pagination.totalRecords}</strong> factures
              </span>
            </div>
          </div>

          {showFilters && (
            <div className="filter-bar">
              <div className="filter-group">
                <label>Statut</label>
                <select name="statut" value={filters.statut} onChange={handleFilterChange}>
                  <option value="">Tous</option>
                  <option value="Envoyée">Envoyée</option>
                  <option value="Payée">Payée</option>
                  <option value="Impayée">Impayée</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Date début</label>
                <input type="date" name="date_debut" value={filters.date_debut} onChange={handleFilterChange} />
              </div>
              <div className="filter-group">
                <label>Date fin</label>
                <input type="date" name="date_fin" value={filters.date_fin} onChange={handleFilterChange} />
              </div>
              <div className="filter-group" style={{ flex: '0 0 auto' }}>
                <button className="monetra-btn primary" onClick={applyFilters} disabled={loading || refreshing}>
                  <FontAwesomeIcon icon={faSearch} /> Appliquer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Grille de cartes */}
        <div className="glass-card p-0 overflow-hidden">
          {loading && !refreshing ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : factures.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucune facture trouvée.</div>
          ) : (
            <div className="cards-grid" style={{ padding: '1rem' }}>
              {factures.map(item => {
                const ht = computeHT(item.montantTotal);
                const tva = computeTVA(item.montantTotal);
                return (
                  <div key={item.id} className="facture-card">
                    <div className="card-header">
                      <span className="card-id">#{item.id}</span>
                      <span className={`statut-badge ${item.statut}`}>{item.statut}</span>
                    </div>
                    <div className="client-name">
                      <Link to={`/client-Profil/${item.idcl}`} className="text-white text-decoration-none">
                      <FontAwesomeIcon icon={faUser} className="text-white" />
                      {item.client_nom || `Client #${item.idcl}`}
                      </Link>
                    </div>
                    <div className="dates">
                      <span><FontAwesomeIcon icon={faCalendarAlt} /> {item.date}</span>
                      <span><FontAwesomeIcon icon={faClock} /> {item.echeance}</span>
                    </div>
                    <div className="montants">
                      <span className="ht">HT: {ht}$</span>
                      <span className="ht">TVA: {tva}$</span>
                      <span className="ttc">{parseFloat(item.montantTotal).toFixed(2)}$</span>
                    </div>
                    {item.details && (
                      <div className="details">{item.details}</div>
                    )}
                    <div className="card-actions">
                      <button className="monetra-btn icon-only" onClick={() => openInvoiceModal(item.id)}>
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="monetra-btn icon-only" onClick={() => openEditModal(item)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="monetra-btn icon-only danger" onClick={() => { setFormData(item); setShowDeleteModal(true); }}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination améliorée */}
          <div className="p-3 border-top border-secondary border-opacity-10 flex-between">
            <div className="info">
              Page <strong>{pagination.currentPage}</strong> sur {pagination.totalPages} ({pagination.totalRecords} enregistrements)
            </div>
            <div className="pagination-buttons">
              <button
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchFactures(1, filters, false)}
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </button>
              <button
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchFactures(pagination.currentPage - 1, filters, false)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {[...Array(pagination.totalPages).keys()].map(num => {
                const p = num + 1;
                if (
                  p === 1 ||
                  p === pagination.totalPages ||
                  (p >= pagination.currentPage - 1 && p <= pagination.currentPage + 1)
                ) {
                  return (
                    <button
                      key={p}
                      className={p === pagination.currentPage ? 'active' : ''}
                      onClick={() => fetchFactures(p, filters, false)}
                    >
                      {p}
                    </button>
                  );
                }
                if (p === pagination.currentPage - 2 || p === pagination.currentPage + 2) {
                  return <span key={p} className="page-ellipsis">…</span>;
                }
                return null;
              })}
              <button
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchFactures(pagination.currentPage + 1, filters, false)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchFactures(pagination.totalPages, filters, false)}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de visualisation */}
      <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} size="lg" centered className="monetra-modal">
        <Modal.Body className="p-0">
          {invoiceId && <InvoiceView factureId={invoiceId} />}
        </Modal.Body>
      </Modal>

      {/* Modale Ajout/Modification */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title><FontAwesomeIcon icon={faEdit} className="text-success me-2" /> {editingFacture ? 'Modifier' : 'Ajouter'} une facture</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">ID Client</label>
                <input className="monetra-input" name="idcl" value={formData.idcl} onChange={(e) => setFormData({...formData, idcl: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Date</label>
                <input type="date" className="monetra-input" name="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Échéance</label>
                <input type="date" className="monetra-input" name="echeance" value={formData.echeance} onChange={(e) => setFormData({...formData, echeance: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Montant TTC ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="montantTotal" value={formData.montantTotal} onChange={(e) => setFormData({...formData, montantTotal: parseFloat(e.target.value)})} required disabled={submitting} />
                <small className="text-muted small">TVA 16% calculée automatiquement</small>
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Statut</label>
                <select className="monetra-input" name="statut" value={formData.statut} onChange={(e) => setFormData({...formData, statut: e.target.value})} disabled={submitting}>
                  <option value="Envoyée">Envoyée</option>
                  <option value="Payée">Payée</option>
                  <option value="Impayée">Impayée</option>
                </select>
              </div>
              <div className="col-12">
                <label className="text-secondary small">Détails</label>
                <textarea className="monetra-input" name="details" rows="3" value={formData.details} onChange={(e) => setFormData({...formData, details: e.target.value})} required disabled={submitting} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn outline" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? <><FontAwesomeIcon icon={faSyncAlt} spin /> Enregistrement...</> : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modale Suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger"><FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer la facture <strong>#{formData.id}</strong> ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn outline" disabled={deleting}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={deleting}>
            {deleting ? <><FontAwesomeIcon icon={faSyncAlt} spin /> Suppression...</> : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modale Import CSV */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleImportCSV}>
          <Modal.Header closeButton>
            <Modal.Title><FontAwesomeIcon icon={faFileCsv} className="text-info me-2" /> Importer CSV</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary small">Colonnes : <code>idcl, date, echeance, montantTotal, statut, details</code></p>
            <input type="file" accept=".csv" className="monetra-input" onChange={(e) => setSelectedFile(e.target.files[0])} required disabled={submitting} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowImportModal(false)} className="monetra-btn outline" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? <><FontAwesomeIcon icon={faSyncAlt} spin /> Importation...</> : 'Importer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageFactures;